import React from "react";
import {CardDetails} from "../../../../enums";
import {formatCurrency} from "../../../../helpers";
import {
  Button,
  Checkbox,
  Container,
  FormControl,
  InputLabel,
  LoadingButton,
  MenuItem,
  Select,
  Text,
} from "../../../../ui";
import {IconAdd} from "../../../bounce-ui";
import {CancellationReason} from "../../../declarations";
import {MethodCard} from "../../manage-payment-method";
import {PaymentMethodType} from "../utils";

export type DelayPaymentDialogResult = "delayed" | "cancelPaymentPlan" | "none";

interface ISelectPaymentMethod {
  hasPaymentMethod: boolean | string | undefined;
  achBlocked: boolean | undefined;
  title?: string;
  paymentMethod: PaymentMethodType;
  paymentMethodOption: Record<string, string>;
  setPaymentMethod: (method: PaymentMethodType) => void;
}

export const SelectPaymentMethod = ({
  hasPaymentMethod,
  achBlocked,
  title,
  paymentMethod,
  paymentMethodOption,
  setPaymentMethod,
}: ISelectPaymentMethod) => {
  if (achBlocked || !hasPaymentMethod) {
    return null;
  }

  return (
    <FormControl variant="outlined" sx={{width: "100%"}}>
      <InputLabel>{title || "Payment method"}</InputLabel>
      <Select
        value={paymentMethod}
        label={title || "Payment method"}
        onChange={(event: any) => setPaymentMethod(event.target.value)}
        sx={{
          height: "50px",
        }}
      >
        {Object.keys(paymentMethodOption).map((method) => (
          <MenuItem key={method} value={method}>
            {paymentMethodOption[method]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IPaymentTermsAndConditions {
  paymentMethod: PaymentMethodType;
  checkedTerms: boolean;
  setCheckedTerms: (checked: boolean) => void;
  TermsAndConditionsApproval: React.ComponentType<any>;
  termsAndConditionsProps: any;
}

export const PaymentTermsAndConditions = ({
  paymentMethod,
  checkedTerms,
  setCheckedTerms,
  TermsAndConditionsApproval,
  termsAndConditionsProps,
}: IPaymentTermsAndConditions) => {
  if (paymentMethod !== "current") {
    return null;
  }

  return (
    <Container row sx={{alignItems: "start", gap: "0px"}}>
      <Checkbox size={"small"} checked={checkedTerms} onClick={() => setCheckedTerms(!checkedTerms)} />
      <TermsAndConditionsApproval {...termsAndConditionsProps} />
    </Container>
  );
};

interface IPaymentLoadingButton {
  paymentMethod: PaymentMethodType;
  submitTitle: string;
  checkedTerms: boolean;
  disable: boolean;
  buttonTouched: boolean | undefined;
  isLoading: boolean | undefined;
  onPaymentSubmit: () => void;
}

export const PaymentLoadingButton = ({
  paymentMethod,
  submitTitle,
  checkedTerms,
  disable,
  buttonTouched,
  isLoading,
  onPaymentSubmit,
}: IPaymentLoadingButton) => {
  return (
    <LoadingButton
      disabled={paymentMethod !== "remove" && (!checkedTerms || disable || buttonTouched)}
      onClick={onPaymentSubmit}
      loading={isLoading}
    >
      {paymentMethod === "remove" ? "Save and update" : submitTitle}
    </LoadingButton>
  );
};

interface IBackToDashboardButton {
  backToDashboardButton: boolean;
  handleBackToDashboard?: () => void;
}

export const BackToDashboardButton = ({backToDashboardButton, handleBackToDashboard}: IBackToDashboardButton) => {
  if (!backToDashboardButton) {
    return null;
  }

  return (
    <Button buttonType={"contained-secondary"} onClick={handleBackToDashboard} fullWidth>
      Back to dashboard
    </Button>
  );
};

interface ISelectPaymentMethodWithWallet {
  achBlocked: boolean | undefined;
  title?: string;
  selectedCardId?: string | number | undefined;
  hasPaymentMethod: boolean;
  allPaymentMethods?: CardDetails[];
  onAdd: () => void;
  setSelectedCardId: (id: string | number | undefined) => void;
  CardMethodMenu: React.FC<{cardDetails: CardDetails}>;
}

export const SelectPaymentMethodWithWallet = ({
  achBlocked,
  title,
  selectedCardId,
  hasPaymentMethod,
  allPaymentMethods,
  onAdd,
  setSelectedCardId,
  CardMethodMenu,
}: ISelectPaymentMethodWithWallet) => {
  if (achBlocked) {
    return null;
  }

  return (
    <Container fullWidth sx={{alignItems: "start", gap: "15px"}}>
      <FormControl variant="outlined" sx={{width: "100%"}}>
        <InputLabel>{title || "Payment method"}</InputLabel>
        <Select
          value={selectedCardId || ""}
          label={title || "Payment method"}
          onChange={(event: any) => {
            const value = event.target.value;
            if (value === "add") {
              onAdd();
            } else {
              setSelectedCardId(value);
            }
          }}
          sx={{
            height: "50px",
          }}
        >
          {hasPaymentMethod &&
            allPaymentMethods?.map((method) => {
              const cardMethod = method as CardDetails;
              return (
                <MenuItem key={cardMethod.id} value={cardMethod.id}>
                  <Container fullWidth>
                    <MethodCard
                      key={cardMethod.id}
                      cardDetails={cardMethod}
                      isSelectLabel
                      CardMethodMenu={CardMethodMenu}
                    />
                  </Container>
                </MenuItem>
              );
            })}
          <MenuItem value={"add"}>
            <Container onClick={onAdd} row sx={{gap: "4px"}}>
              <IconAdd /> Add a payment method
            </Container>
          </MenuItem>
        </Select>
      </FormControl>
    </Container>
  );
};

export interface CancellationReasonOption {
  id: CancellationReason;
  label: string;
}

export const CANCELLATION_REASON_OPTIONS: CancellationReasonOption[] = [
  {id: "payment_date_doesnt_work", label: "The payment date doesn't work"},
  {id: "payment_amount_too_high", label: "The payment amount is too high"},
  {id: "need_payment_break", label: "I need a break from payments"},
  {id: "other", label: "Something else"},
];

export const getCancellationWarningParagraphs = (originalBalance?: number) => {
  return {
    first: "We encourage you to speak with an expert to find a solution that better works for you.",
    second: (
      <span>
        If you cancel, you&apos;ll give up your current plan&apos;s terms and your amount due will be{" "}
        <Text variant="interDisplay600" sx={{display: "inline"}}>
          {originalBalance != null ? formatCurrency(originalBalance) : ""}
        </Text>
        .
      </span>
    ),
  };
};
